import { usePage } from '@inertiajs/react';
import { useViewportSize } from '@mantine/hooks';
import classNames from 'classnames';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import shuffle from 'lodash/shuffle';
import { Fragment, ReactNode, useCallback, useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, ArrowRightCircle, Check, Heart, Minus, Plus, Zap } from 'react-feather';

import { prettifyUrl } from '../../Shared/helpers';
import { useLocale } from '../../Shared/locale';
import { Event } from '../../Shared/types';
import { AButton, Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import { HTML } from '../../Shared/UI/HTML';
import { Icon } from '../../Shared/UI/Icon';
import { PickerItem } from '../../Shared/UI/Picker';
import Container from '../UI/Container';
import Layout from '../UI/Layout';
import ConnectedCharitiesSection from './ConnectedCharitiesSection';
import PopularEventsSection from './PopularEventsSection';

const backgroundClassNames = {
  white: 'bg-white',
  slate: 'bg-slate-50 relative z-10 my-[-1px]',
  emerald: 'bg-emerald-50 relative z-10 my-[-1px]',
};

const icons = {
  check: () => <Check />,
  arrow_right: () => <ArrowRight />,
  arrow_right_circle: () => <ArrowRightCircle />,
  heart: () => <Heart />,
  zap: () => <Zap />,
};

interface TextContentType {
  type: 'text';
  text: string;
}

interface CardContentType {
  type: 'card';
  label: string;
  content: string;
  cta_text: string;
  cta_url: string;
  text_position: { value: 'left' | 'right'; };
  image: { id: string; url: string; alt: string | null; width: number; height: number; } | null;
  background: { value: 'slate' | 'white' | 'emerald' | 'blue' | 'image'; };
}

interface CtasContentType {
  type: 'ctas';
  align: { value: 'left' | 'center' | 'right'; };
  ctas: {
    id: string;
    cta_text: string;
    cta_url: string;
    variant: { value: 'primary' | 'secondary' | 'tertiary'; };
    target: { value: '_self' | '_blank'; };
  }[];
}

interface FaqsContentType {
  type: 'faqs';
  faqs: {
    id: string;
    title: string;
    content: string | null;
  }[];
}

interface UspsContentType {
  type: 'usps';
  align: { value: 'left' | 'center' | 'right'; };
  usps: {
    id: string;
    icon: { value: keyof typeof icons; };
    content: string;
  }[];
}

interface CompositeHeaderContentType {
  type: 'composite_header';
  images: { id: string; url: string; alt: string | null; width: number; height: number; }[];
}

interface QuotesContentType {
  type: 'quotes';
  count: number;
  quotes: {
    id: string;
    title: string;
    content: string;
    thumbnail: { id: string; url: string; alt: string | null; width: number; height: number; } | null;
  }[];
}

interface SitesContentType {
  type: 'sites';
  count: number;
  sites: {
    id: string;
    title: string;
    website: { url: string; };
    image: { id: string; url: string; alt: string | null; width: number; height: number; };
  }[];
}

interface ConnectedCharitiesContentType {
  type: 'connected_charities';
  destination: { value: 'overview' | 'create'; };
}

interface PopularEventsContentType {
  type: 'popular_events';
}

interface HtmlContentType {
  type: 'html';
  desktop: {
    value: string | null;
  };
  mobile: {
    value: string | null;
  };
}

type ContentType = TextContentType | CardContentType | CtasContentType | UspsContentType | FaqsContentType | CompositeHeaderContentType | QuotesContentType | SitesContentType | ConnectedCharitiesContentType | PopularEventsContentType | HtmlContentType;

export interface Section {
  id: string;
  background: { value: 'white' | 'slate'; };
  container_width: { value: 'sm' | 'md' | 'lg'; };
  spacing: { value: 'sm' | 'md' | 'lg' | 'xl'; };
  content: ContentType[];
}

export interface ContentProps {
  content: Section[];
  charities: (PickerItem & { logo_url: string | null; slug: string; })[];
  popularEvents: Event[];
}

export default function Content({
  content,
  charities,
  popularEvents,
}: ContentProps) {
  return (
    <div className="relative">
      {content.map((section, index) => (
        <Fragment key={section.id}>
          {index > 0 && (
            <hr className="border-slate-150 relative" />
          )}
          <div className={classNames(backgroundClassNames[section.background.value])}>
            <Container
              size={section.container_width.value}
              spacing={section.spacing.value}
            >
              <div className="space-y-8">
                {section.content.map((content, index) => (
                  <Fragment key={index}>
                    {content.type === 'text' && (
                      <TextContent content={content} />
                    )}
                    {content.type === 'html' && (
                      <HtmlContent content={content} />
                    )}
                    {content.type === 'ctas' && (
                      <CtasContent content={content} />
                    )}
                    {content.type === 'faqs' && (
                      <FaqsContent content={content} />
                    )}
                    {content.type === 'usps' && (
                      <UspsContent content={content} />
                    )}
                    {content.type === 'card' && (
                      <CardContent content={content} />
                    )}
                    {content.type === 'composite_header' && (
                      <CompositeHeaderContent content={content} />
                    )}
                    {content.type === 'quotes' && (
                      <QuotesContent content={content} />
                    )}
                    {content.type === 'connected_charities' && (
                      <ConnectedCharitiesSection charities={charities} destination={content.destination.value} />
                    )}
                    {content.type === 'popular_events' && (
                      <PopularEventsSection events={popularEvents} />
                    )}
                    {content.type === 'sites' && (
                      <SitesContent content={content} />
                    )}
                  </Fragment>
                ))}
              </div>
            </Container>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

interface TextContentProps {
  content: TextContentType;
}

const TextContent = ({ content }: TextContentProps) => {
  const { props: { charities } } = usePage<{ charities: PickerItem[];}>();
  const { formatNumber } = useLocale();

  const value = content.text
    .replace(/style="text-align: center;/g, 'class="text-center text-balance"') // Avoid CSP errors.
    .replace('{{charities_count}}', formatNumber(charities.length));

  return (
    <HTML
      value={value}
      className="[&>p:not(:last-child)]:mb-6 [&_ul]:ml-4 [&_ul_li]:pl-1 [&>ul:not(:last-child)]:mb-6 [&_ol]:ml-4 [&_ol_li]:pl-1 [&>ol:not(:last-child)]:mb-6 [&>blockquote:not(:last-child)]:mb-6 [&_h1]:text-3xl [&_h2]:text-2xl [&_h3]:text-lg [&_h1]:mb-4 [&_h2]:mb-2 [&_h3]:mb-1 [&_hr]:my-8 [&_hr]:border-slate-250 [&_blockquote]:text-2xl [&_blockquote]:font-light [&_blockquote]:text-balance [&_blockquote]:text-slate-600 [&_table]:w-full [&_table]:text-slate-500 [&_table]:text-base [&_img]:block [&_img]:w-full [&_img]:rounded-xl [&_img]:max-w-3xl [&_img]:mx-auto"
    />
  );
};

interface CardContentProps {
  content: CardContentType;
}

const CardContent = ({ content }: CardContentProps) => {
  const bgClasses = {
    slate: 'bg-slate-100',
    white: 'bg-white shadow-lg',
    emerald: 'bg-emerald-50',
    blue: 'bg-blue-100',
    image: 'bg-white',
  } as const;

  const labelClasses = {
    slate: 'bg-slate-200',
    white: 'bg-slate-200',
    emerald: 'bg-emerald-100 text-emerald-600',
    blue: 'bg-blue-200 text-blue-600',
    image: 'bg-white/50',
  } as const;

  const buttonVariants = {
    slate: 'primary',
    white: 'primary',
    emerald: 'primary',
    blue: 'secondary',
    image: 'primary',
  } as const;

  return (
    <div
      className={classNames(
        'rounded-xl relative',
        bgClasses[content.background.value],
      )}
    >
      {content.image && content.background.value === 'image' && (
        <img
          src={content.image.url}
          alt={content.image.alt || ''}
          className="absolute inset-0 w-full h-full object-cover rounded-xl"
        />
      )}
      <div
        className={classNames(
          'relative p-8 sm:p-16 rounded-xl',
          content.image && 'grid sm:grid-cols-2 gap-8 items-center',
          content.image && content.background.value === 'image' && 'text-white [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_a]:text-white min-h-64 grid-cols-2',
          (content.content || content.cta_url)
            && content.background.value === 'image'
            && (content.text_position.value === 'left' ? 'bg-gradient-to-r from-black/40 from-10% via-black/30 to-70%' : 'bg-gradient-to-l from-black/40 from-10% via-black/30 to-70% ')
        )}
      >
        {content.image && content.background.value !== 'image' && (
          <div className={classNames('flex justify-center', content.text_position.value === 'left' && 'sm:order-2')}>
            <img
              src={content.image.url}
              alt={content.image.alt || ''}
              className="sm:-my-4 w-full max-w-[250px] sm:max-w-[350px]"
              style={{ aspectRatio: content.image.width / content.image.height }}
            />
          </div>
        )}
        <div className={classNames(content.background.value === 'image' && 'pt-12')}>
          {content.label && (
            <div className="mb-3">
              <span className={classNames('rounded inline-block py-1 px-2 font-medium mr-2 text-xs', labelClasses[content.background.value])}>
                {content.label}
              </span>
            </div>
          )}
          <TextContent content={{ type: 'text', text: content.content || '' }} />
          {content.cta_text && content.cta_url && (
            <div className="mt-4">
              <AButton
                href={content.cta_url}
                variant={buttonVariants[content.background.value]}
              >
                {content.cta_text}
              </AButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface CtasContentProps {
  content: CtasContentType;
}

interface CtasContentProps {
  content: CtasContentType;
}

const CtasContent = ({ content }: CtasContentProps) => (
  <div
    className={classNames(
      '-my-2',
      content.align.value === 'center' && 'text-center',
      content.align.value === 'right' && 'text-right',
    )}
  >
    {content.ctas.map((cta) => (
      <AButton
        href={cta.cta_url}
        variant={cta.variant.value}
        className={classNames('mx-1 my-1', cta.variant.value === 'tertiary' && '!text-cyan-600 hover:!text-cyan-500')}
        target={cta.target.value}
        rel={cta.target.value === '_blank' ? 'noopener noreferrer' : undefined}
        key={cta.id}
      >
        {cta.cta_text}
        {cta.variant.value === 'tertiary' && (
          <Icon className="ml-1">
            <ArrowRight />
          </Icon>
        )}
      </AButton>
    ))}
  </div>
);

interface FaqsContentProps {
  content: FaqsContentType;
}

const FaqsContent = ({ content }: FaqsContentProps) => (
  <div>
    {content.faqs.map((faq) => (
      <FaqItem faq={faq} key={faq.id} />
    ))}
  </div>
);

interface FaqItemProps {
  faq: FaqsContentType['faqs'][0];
}

const FaqItem = ({ faq }: FaqItemProps) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-slate-250 pt-4" key={faq.id}>
      <h3 className="text-lg font-medium">
        <a
          onClick={() => setExpanded((expanded) => !expanded)}
          role="button"
          className="py-4 text-slate-800 hover:text-slate-800 flex items-center justify-between [&:hover_svg]:text-emerald-500"
        >
          <span className="mr-3">
            {faq.title}
          </span>
          <Icon className="text-slate-500 mr-2">
            {expanded ? <Minus /> : <Plus />}
          </Icon>
        </a>
      </h3>
      <div
        className={classNames(
          'overflow-hidden transition-opacity mb-4',
          expanded ? 'h-auto opacity-100' : 'h-0 opacity-0'
        )}
      >
        <div className="pb-4">
          <TextContent content={{ type: 'text', text: faq.content || '' }} />
        </div>
      </div>
    </div>
  );
};

interface UspsContentProps {
  content: UspsContentType;
}

const UspsContent = ({ content }: UspsContentProps) => (
  <ul
    className={classNames(
      'space-y-2 sm:space-y-0 sm:flex sm:space-x-4 text-slate-500',
      content.align.value === 'center' && 'justify-center',
      content.align.value === 'right' && 'justify-end',
    )}
  >
    {content.usps.map((usp) => (
      <li className="flex items-center" key={usp.id}>
        {usp.icon.value && (
          <Icon className="mr-2 text-emerald-500">
            {icons[usp.icon.value]()}
          </Icon>
        )}
        <TextContent content={{ type: 'text', text: usp.content }} />
      </li>
    ))}
  </ul>
);

interface CompositeHeaderContentProps {
  content: CompositeHeaderContentType;
}

const CompositeHeaderContent = ({ content }: CompositeHeaderContentProps) => {
  const firstRow = content.images.slice(0, 3);
  const secondRow = content.images.slice(3);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-[4fr_4fr_6fr] items-end gap-3 max-w-3xl mx-auto px-[5%]">
        {firstRow.map((image) => (
          <div key={image.id}>
            <img
              src={image.url} alt={image.alt || ''}
              className="w-full rounded-xl"
              style={{ aspectRatio: image.width / image.height }}
            />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4 items-start gap-3 max-w-3xl mx-auto">
        {secondRow.map((image) => (
          <div key={image.id}>
            <img
              src={image.url} alt={image.alt || ''}
              className="w-full rounded-xl"
              style={{ aspectRatio: image.width / image.height }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

interface QuotesContentProps {
  content: QuotesContentType;
}

const QuotesContent = ({ content }: QuotesContentProps) => {
  const [quotes] = useState(shuffle(content.quotes).slice(0, 3));
  const rotation = [-2, 2, -3];

  return (
    <div className="grid sm:grid-cols-3 gap-8 items-center py-4">
      {quotes.map((quote, index) => (
        <div key={quote.id}>
          <Card style={{ transform: `rotate(${rotation[index]}deg)` }} spacing="sm" className="!shadow-lg">
            <h4 className="font-bold mb-3 flex items-center space-x-3">
              {quote.thumbnail && (
                <div className="w-7 h-7">
                  <img
                    src={quote.thumbnail.url}
                    alt={quote.thumbnail.alt || ''}
                    className="object-cover w-full h-full rounded-full"
                  />
                </div>
              )}
              <span>
                {quote.title}
              </span>
            </h4>
            <div>
              <TextContent content={{ type: 'text', text: quote.content || '' }} />
            </div>
          </Card>
        </div>
      ))}
    </div>
  );
};

interface SitesContentProps {
  content: SitesContentType;
}

const SitesContent = ({ content }: SitesContentProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: 'start', slidesToScroll: 'auto', dragFree: true,
    },
    [WheelGesturesPlugin()]
  );

  const [prevButtonDisabled, setPrevButtonDisabled] = useState(true);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);

  const nextSlide = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    emblaApi.scrollNext();
  }, [emblaApi]);

  const prevSlide = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevButtonDisabled(!emblaApi.canScrollPrev());
    setNextButtonDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    onSelect(emblaApi);

    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div>
      <div className="embla overflow-hidden -mx-6 px-6 sm:-mx-8 sm:px-8 xl:mx-0 xl:px-0" ref={emblaRef}>
        <div className="embla__container flex space-x-6">
          {content.sites.map((site) => (
            <a
              href={`${site.website.url}?utm_source=supporta.com`}
              className="embla__slide aspect-square relative rounded-xl overflow-hidden flex items-end justify-center hover:pb-3 transition-all max-w-[286px]"
              target="_blank"
              rel="noopener noreferrer"
              style={{ flex: '0 0 100%' }}
              key={site.id}
            >
              <img
                src={site.image.url}
                alt={site.image.alt || ''}
                className="absolute inset-0 object-cover w-full h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900 via-blue-800/40 via-30" />
              <div
                className="relative text-center p-6 text-white"
                style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)' }}
              >
                <h4 className="text-white font-bold text-xl text-balance">
                  {site.title}
                </h4>
                {prettifyUrl(site.website.url)}
              </div>
            </a>
          ))}
        </div>
      </div>
      <div className="text-center space-x-3 mt-6">
        <Button
          onClick={prevSlide}
          variant="secondary"
          disabled={prevButtonDisabled}
          className="!rounded-full !text-xl"
          size="lg"
          iconButton
        >
          <Icon>
            <ArrowLeft />
          </Icon>
        </Button>
        <Button
          onClick={nextSlide}
          variant="secondary"
          disabled={nextButtonDisabled}
          className="!rounded-full !text-xl"
          size="lg"
          iconButton
        >
          <Icon>
            <ArrowRight />
          </Icon>
        </Button>
      </div>
    </div>
  );
};

interface HtmlContentProps {
  content: HtmlContentType;
}

const HtmlContent = ({ content }: HtmlContentProps) => {
  const { width } = useViewportSize();

  if (width >= 768) {
    return <TextContent content={{ type: 'text', text: content.desktop.value || content.mobile.value || '' }} />;
  }

  return <TextContent content={{ type: 'text', text: content.mobile.value || content.desktop.value || '' }} />;
};

Content.layout = (page: ReactNode) => <Layout>{page}</Layout>;
